/**
 * ============================================================================
 * EXECUTIVE SUMMARY PAGE — top-level route page composing the full dashboard.
 * ============================================================================
 *
 *
 * SCOPING: the demand-category checkbox row is the ONLY scoping mechanism —
 * the heading is static and the canonical route is /metrics. /network/:id is
 * kept as a redirect that pre-selects that checkbox (link stability).
 *
 * LAYOUT (matches Figma top-to-bottom):
 *   [Header: "FAI - WBR Detailed Summary" + RefreshStatusBadge]
 *   [FilterBar: chip tokens + Clear/Add/Compare/Save buttons]
 *   [DashboardTabs: Executive Summary | Deep Dive | WBR Snapshot | …]
 *   [Tab content: <ExecutiveSummaryTab> / <DeepDiveTab> / <GlossaryTab>]
 *
 * SNAPSHOT: not a separate tab — the page-level Live/Snapshot toggle switches
 * the Executive Summary between live data and the frozen WBR capture.
 *
 * TAB COMPONENT REGISTRY: maps view-config JSON `type` strings to React
 * components. Adding a new tab type = add one line here + one component in
 * src/components/tabs/. Adding a tab to an existing type = edit the JSON only.
 *
 * DATA FETCH: one call to useGetDataQuery per the whole page — all tabs and
 * tables draw from the same cached rowset (bulk-fetch-then-derive pattern).
 * Pass `rows` down or access from the RTK cache selector; do NOT fetch per tab.
 *
 * URL SYNC (TODO follow-up): activeTabId should sync to a query param
 * (?tab=executive-summary) so links are shareable within the WBR meeting.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Button from '@amzn/awsui-components-core/button';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import { PageHeader } from 'src/components/layout/PageHeader';
import { DashboardTabs } from 'src/components/layout/DashboardTabs';
import { RefreshStatusBadge } from 'src/components/layout/RefreshStatusBadge';
import { FilterBar } from 'src/components/filters/FilterBar';
import { DemandCategoryCheckboxes } from 'src/components/filters/DemandCategoryCheckboxes';
import { WeekControls } from 'src/components/filters/WeekControls';
import { ExecutiveSummaryTab } from 'src/components/tabs/ExecutiveSummaryTab';
import { DeepDiveTab } from 'src/components/tabs/DeepDiveTab';
import { GlossaryTab } from 'src/components/tabs/GlossaryTab';
import DataModeSelector from 'src/components/dashboard/DataModeSelector';
import detailedSummaryConfig from 'src/config/views/detailedSummaryView.json';
import { WbrView, MetricTableSectionConfig, NetworkId } from 'src/types';
import { useAppDispatch, useAppSelector } from 'src/app/store';
import { demandCategoriesReplaced, dataModeChanged } from 'src/features/dashboard/dashboardSlice';
import { DEMAND_CATEGORY_IDS } from 'src/config/demandCategories';

const viewConfig = detailedSummaryConfig as unknown as WbrView;

/**
 * Registry: view-config `type` → component. The component receives the tab's
 * `sections` as a prop (populated for 'table-sections' type, undefined for others).
 */
const TAB_COMPONENTS: Record<string, React.FC<{ sections?: MetricTableSectionConfig[] }>> = {
  'table-sections': ExecutiveSummaryTab,
  'deep-dive': DeepDiveTab,
  glossary: GlossaryTab,
};

export const ExecutiveSummaryPage: React.FC = () => {
  const { networkId } = useParams<{ networkId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const dataMode = useAppSelector((state) => state.dashboard.dataMode);

  // LEGACY ROUTE: /network/:networkId predates checkbox-only scoping. Landing
  // there selects that category's checkbox (known ids only) and forwards to
  // the canonical /metrics — old links keep working, URLs are never removed.
  useEffect(() => {
    if (networkId) {
      if ((DEMAND_CATEGORY_IDS as string[]).includes(networkId)) {
        dispatch(demandCategoriesReplaced([networkId as NetworkId]));
      }
      navigate('/metrics', { replace: true });
    }
  }, [networkId, dispatch, navigate]);

  // TODO(follow-up): sync to URL search param (?tab=…) for shareable links.
  const [activeTabId, setActiveTabId] = useState(viewConfig.tabs[0]?.id || '');

  const activeTab = viewConfig.tabs.find((t) => t.id === activeTabId);
  const TabComponent = activeTab ? TAB_COMPONENTS[activeTab.type] : null;

  return (
    <SpaceBetween direction="vertical" size="l">
      {/* Top bar: header + refresh badge on the left, back link + Live/Snapshot toggle pinned right */}
      <div className="page-top-bar">
        <SpaceBetween direction="horizontal" size="m">
          <PageHeader />
          <RefreshStatusBadge />
        </SpaceBetween>
        <SpaceBetween direction="horizontal" size="s">
          <Button variant="link" iconName="arrow-left" onClick={() => navigate('/detail')}>
            FAI - WBR Customer Domain Level Summary
          </Button>
          <DataModeSelector
            mode={dataMode}
            onModeChange={(m) => dispatch(dataModeChanged(m))}
          />
        </SpaceBetween>
      </div>

      {/* Tab navigation (driven by view config JSON) */}
      <DashboardTabs
        tabs={viewConfig.tabs}
        activeTabId={activeTabId}
        onTabChange={setActiveTabId}
      />

      {/* Network checkbox row ABOVE the filter system (QuickSight control-bar
          order, spec R10.1) — table-sections tabs only. */}
      {activeTab?.type === 'table-sections' && <DemandCategoryCheckboxes />}

      {/* Filter system: chip bar + week window controls (hidden on glossary
          since it's a static reference). No Fragment here — SpaceBetween
          flattens Fragment children in React 18 and warns (each child must
          be a direct element). */}
      {activeTab?.type !== 'glossary' && <FilterBar />}
      {activeTab?.type !== 'glossary' && <WeekControls />}

      {/* Tab content — rendered BELOW tabs, not inside them */}
      {TabComponent && <TabComponent sections={activeTab?.sections} />}
    </SpaceBetween>
  );
};
